import React, { useCallback, useEffect, useState } from "react";
import SectionTitle from "../../Components/SectionTitle";
import { Link } from "react-router-dom";
import { Button, Image, Input, message, Table } from "antd";
import { PiPlus } from "react-icons/pi";
import { ArticleColumns, ArticleTableDataType } from "./types";
import { CLOUD_URL } from "../../lib/env";
import { deleteArticle, getArticles } from "./action";
import { getCounter } from "../../lib/functions";
import DashModal from "../../Components/DashModal";
import AddNewArticle from "./Components/AddNewArticle";
import EditArticle from "./Components/EditTitle";
import KeywordManager from "./Components/KeywordManager";
import EditContent from "./Components/EditContent";
import EditArticleImage from "./Components/EditArticleImage";
const { Search } = Input;

type Props = {
  addNewArticle?: boolean;
  editArticle?: boolean;
  editKeywords?: boolean;
  editContent?: boolean;
  editImage?: boolean;
};

const Articles = (props: Props) => {
  const [data, setData] = useState<ArticleTableDataType[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpand = (expanded: boolean, record: ArticleTableDataType) => {
    setExpandedRowKeys(expanded ? [record.id] : []);
  };
  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("Articles");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const websites = await getArticles(pageSize, page, query);
      const formatted = websites.map((wb) => ({
        id: wb.id,
        title: wb.MainInfo.Title,
        image: wb.Image,
        content: wb.Content,
        keywords: wb.Keywords,
      }));
      setData(formatted);
    } catch (error) {
      message.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  const handleDelete = async (id: string) => {
    try {
      await deleteArticle(id);
      message.success("Website deleted");
      fetchData();
      fetchCount();
    } catch (error) {
      message.error("Failed to delete article");
    }
  };

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className=" space-y-5">
      <SectionTitle title="Articles" />
      <div className="flex justify-between items-center">
        <Link to={`/articles/add?page=${page + 1}&pageSize=${pageSize}`}>
          <Button type="primary" icon={<PiPlus />}>
            Add New Article
          </Button>
        </Link>
        <Search
          allowClear
          placeholder="Search articles..."
          className="w-1/5"
          onSearch={(value) => setQuery(value)}
        />
      </div>{" "}
      <Table<ArticleTableDataType>
        rowKey="id"
        columns={ArticleColumns(page, pageSize, handleDelete)}
        dataSource={data}
        loading={loading}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <Image width={200} src={CLOUD_URL + "/" + record.image} />
          ),
          rowExpandable: () => true,
          onExpand: handleExpand,
          expandedRowKeys,
        }}
      />
      {props.addNewArticle && (
        <DashModal
          title="Add New Article"
          width={1200}
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewArticle fetchData={fetchData} />
        </DashModal>
      )}
      {props.editArticle && (
        <DashModal
          title="Edit Article"
          width={1200}
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditArticle fetchData={fetchData} />
        </DashModal>
      )}
      {props.editKeywords && (
        <DashModal
          title="Edit Article"
          width={1200}
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <KeywordManager fetchData={fetchData} />
        </DashModal>
      )}
      {props.editContent && (
        <DashModal
          title="Edit Content"
          width={1200}
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditContent fetchData={fetchData} />
        </DashModal>
      )}
      {props.editImage && (
        <DashModal
          title="Edit Image"
          width={600}
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditArticleImage fetchData={fetchData} />
        </DashModal>
      )}
    </div>
  );
};

export default Articles;
